import React, { createContext, useContext, useState, useEffect } from 'react';
import config from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Clear any existing dev user data
    const existingUser = localStorage.getItem('authUser');
    if (existingUser) {
      try {
        const userData = JSON.parse(existingUser);
        if (userData.email === 'dev@xpheno.com' || userData.credential === 'dev-token') {
          console.log('🧹 Clearing development user data');
          localStorage.removeItem('authUser');
        }
      } catch (error) {
        localStorage.removeItem('authUser');
      }
    }

    // Load the Google Identity Services script
    const loadGoogleScript = () => {
      if (window.google) {
        initializeGoogleAuth();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      document.head.appendChild(script);
    };

    const initializeGoogleAuth = () => {
      if (window.google && config.GOOGLE_CLIENT_ID !== 'your-google-client-id-here') {
        try {
          console.log('Initializing Google Auth with Client ID:', config.GOOGLE_CLIENT_ID);
          window.google.accounts.id.initialize({
            client_id: config.GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: true
          });
          setIsInitialized(true);
          console.log('Google Auth initialized successfully');
        } catch (error) {
          console.error('Google Auth initialization failed:', error);
        }
      }
      setLoading(false);
    };

    // Check if user is already logged in (from localStorage)
    const savedUser = localStorage.getItem('authUser');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        localStorage.removeItem('authUser');
      }
    }

    loadGoogleScript();
  }, []);

  const handleCredentialResponse = (response) => {
    try {
      // Decode the JWT token to get user information
      const decoded = parseJwt(response.credential);
      
      // Check if the email domain is allowed
      if (!isEmailAllowed(decoded.email)) {
        alert('Access denied. Only @xpheno.com emails are allowed.');
        return;
      }

      const userData = {
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        credential: response.credential
      };

      setUser(userData);
      localStorage.setItem('authUser', JSON.stringify(userData));
    } catch (error) {
      console.error('Error processing credential response:', error);
      alert('Authentication failed. Please try again.');
    }
  };

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new Error('Invalid token');
    }
  };

  const isEmailAllowed = (email) => {
    const domain = email.split('@')[1];
    
    // Check domain - only allow emails from the configured domain
    if (domain !== config.ALLOWED_DOMAIN) {
      return false;
    }

    // All domain checks pass - the backend will handle specific email authorization
    // by checking the authorized_users database table
    return true;
  };

  const signIn = () => {
    if (window.google && isInitialized) {
      window.google.accounts.id.prompt();
    } else {
      alert('Google authentication is not initialized. Please refresh the page.');
    }
  };

  const signOut = () => {
    setUser(null);
    localStorage.removeItem('authUser');
    
    // Sign out from Google
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  const value = {
    user,
    loading,
    isInitialized,
    signIn,
    signOut,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 